// Captain skills (FTL crew-skill lineage): the captain sharpens with use.
// Six disciplines, three pips each, each trained by a distinct player
// behavior — GUNNERY by kills, PILOTING by distance flown, ENGINEERING by
// damage absorbed, DEADEYE by manual-turret hits, SALVAGE by pickups,
// DARING by early wave calls and flawless waves. Pips feed small run-scoped
// passive mods and die with the captain (permadeath: no carry-over).
// Deterministic: accrual derives from sim events and positions only.

export type SkillId = 'gunnery' | 'piloting' | 'engineering' | 'deadeye' | 'salvage' | 'daring';
export const SKILL_IDS: readonly SkillId[] = [
  'gunnery', 'piloting', 'engineering', 'deadeye', 'salvage', 'daring',
];
export const SKILL_MAX_PIPS = 3;

/** Raw XP thresholds per pip: 100 / 1,000 / 10,000 — a slow, meaningful
 * arc (pip 1 lands around wave 3–5, pip 2 deep in the run, pip 3 is
 * legendary). Same ladder for every discipline; rates below shape the
 * per-skill pace. */
const PIP_THRESHOLDS = [100, 1000, 10000] as const;

/** XP awarded per behavior unit. */
const XP_PER_KILL = 1;
const XP_PER_UNIT_FLOWN = 1 / 25; // 1 xp per 25 arena units traveled
const XP_PER_DAMAGE_POINT = 1 / 5; // 1 xp per 5 damage sustained
const XP_PER_TURRET_HIT = 2; // manual cannon connects are rarer than kills
const XP_PER_PICKUP = 1 / 4; // motes are everywhere; salvage is a slow burn
const XP_PER_BRAVERY = 4; // calling a wave early
const XP_PER_FLAWLESS = 6; // zero damage through a whole wave

export interface CaptainSkills {
  xp: Record<SkillId, number>;
  pips: Record<SkillId, number>;
}

export function freshCaptainSkills(): CaptainSkills {
  return {
    xp: { gunnery: 0, piloting: 0, engineering: 0, deadeye: 0, salvage: 0, daring: 0 },
    pips: { gunnery: 0, piloting: 0, engineering: 0, deadeye: 0, salvage: 0, daring: 0 },
  };
}

/** Add raw XP; returns the new pip count when this award leveled the skill. */
function award(skills: CaptainSkills, id: SkillId, amount: number): number | null {
  if (skills.pips[id] >= SKILL_MAX_PIPS) {
    return null;
  }
  skills.xp[id] += amount;
  const next = PIP_THRESHOLDS[skills.pips[id]];
  if (next !== undefined && skills.xp[id] >= next) {
    skills.pips[id] += 1;
    return skills.pips[id];
  }
  return null;
}

export function noteKill(skills: CaptainSkills): number | null {
  return award(skills, 'gunnery', XP_PER_KILL);
}

export function noteDistance(skills: CaptainSkills, units: number): number | null {
  return award(skills, 'piloting', units * XP_PER_UNIT_FLOWN);
}

export function noteDamageAbsorbed(skills: CaptainSkills, damage: number): number | null {
  return award(skills, 'engineering', damage * XP_PER_DAMAGE_POINT);
}

export function noteTurretHit(skills: CaptainSkills): number | null {
  return award(skills, 'deadeye', XP_PER_TURRET_HIT);
}

export function notePickup(skills: CaptainSkills): number | null {
  return award(skills, 'salvage', XP_PER_PICKUP);
}

export function noteBravery(skills: CaptainSkills): number | null {
  return award(skills, 'daring', XP_PER_BRAVERY);
}

export function noteFlawless(skills: CaptainSkills): number | null {
  return award(skills, 'daring', XP_PER_FLAWLESS);
}

/** Per-pip passive mods, folded into the captain's effect set at level-up. */
export const SKILL_PIP_MODS = {
  gunnery: { damageMult: 0.03, fireRateMult: 0.015 },
  piloting: { moveSpeedMult: 0.02 },
  engineering: { shieldRegenMultBonus: 0.04 },
  deadeye: { damageMult: 0.02 },
  salvage: { magnetRadiusMult: 0.03 },
  daring: { dashHasteBonus: 0.03 },
} as const;

/** Display metadata for the HUD row (HackerNoon pixel icons). */
export const SKILL_META: Record<SkillId, { icon: string; label: string; blurb: string }> = {
  gunnery: { icon: 'fire-solid', label: 'GUN', blurb: '+dmg +rate' },
  piloting: { icon: 'bolt', label: 'PIL', blurb: '+speed' },
  engineering: { icon: 'cog', label: 'ENG', blurb: '+shield' },
  deadeye: { icon: 'eye-solid', label: 'TAC', blurb: '+dmg' },
  salvage: { icon: 'coins-solid', label: 'SAL', blurb: '+magnet' },
  daring: { icon: 'startups', label: 'DAR', blurb: '+dash' },
};

const MOD_LABELS: Record<string, string> = {
  damageMult: 'DMG',
  fireRateMult: 'RATE',
  moveSpeedMult: 'SPEED',
  shieldRegenMultBonus: 'SHIELD',
  magnetRadiusMult: 'MAGNET',
  dashHasteBonus: 'DASH',
};

/**
 * Level-up announcement line: what the new pip actually buys, cumulative —
 * e.g. gunnery ★2 → "+6% DMG +3% RATE". Shown in the level toast so a pip
 * never lands silently.
 */
export function skillEffectSummary(skill: SkillId, pips: number): string {
  const mods = SKILL_PIP_MODS[skill] as Record<string, number>;
  return Object.entries(mods)
    .map(([key, value]) => `+${Math.round(value * pips * 100)}% ${MOD_LABELS[key] ?? key}`)
    .join(' ');
}
